// CSR-1940 (D4) — Date-range filter for the Insured Activity Timeline. A single
// range popover (one Calendar in `mode="range"`) + Clear. Dumb value/onChange so
// `applyTimelineFilters` stays the single filtering truth. Always rendered
// (layout stability); the trigger shows the selected range or "All dates".

import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface Props {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
}

function labelFor(range?: DateRange): string {
  if (range?.from && range.to) {
    return `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`;
  }
  if (range?.from) return `${format(range.from, "MMM d, yyyy")} – …`;
  return "All dates";
}

export function TimelineDateRangeFilter({ value, onChange }: Props) {
  const hasRange = !!value?.from;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 cursor-pointer gap-1.5 text-xs font-medium"
          data-testid="timeline-date-filter"
          aria-label="Filter by date range"
        >
          <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
          {labelFor(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="range"
          selected={value}
          onSelect={onChange}
          numberOfMonths={1}
          autoFocus
        />
        <div className="flex justify-end border-t border-border p-2">
          <Button
            variant="ghost"
            size="sm"
            className="cursor-pointer text-xs"
            disabled={!hasRange}
            onClick={() => onChange(undefined)}
            data-testid="timeline-date-clear"
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
